import {Chess} from 'chess.js';

// DEVIATION from the plan: relative import instead of the '@' alias. This
// import is type-only (CourseChapter/CourseMove/CoursePage/VideoSegment are
// only ever used in type positions in this file), so babel would likely elide
// the runtime require anyway — but jest still has no moduleNameMapper for '@'
// (see useGeneratedCourse.ts), and jest.config.js may not be touched, so a
// relative path is used to remove any doubt.
import {
  ChapterRoleName,
  CourseChapter,
  CourseMove,
  CoursePage,
  CoursePageConfig,
  VideoSegment,
} from '../../Hooks/LmsCourseApi/types';

export type MoveOutcome =
  | {status: 'wrong'}
  | {
      status: 'reply';
      fen: string;
      /**
       * The position after the student's move but before the reply. The UI
       * shows this first and lands the reply a beat later, so the opponent
       * does not appear to move at the same instant as the student.
       */
      afterStudentFen: string;
      replyMove: string;
      studentSan: string;
      replySan: string;
      nextPly: number;
    }
  | {status: 'solved'; fen: string; studentSan: string};

export function isVideoPage(page: CoursePage) {
  return page?.content_type === 'video';
}

export function isPuzzlePage(page: CoursePage) {
  return page?.content_type === 'chess_position';
}

/**
 * A generated course points every video chapter at the same recording,
 * distinguished by this window. A page without one is not playable as a slice.
 */
export function getVideoSegment(page: CoursePage): VideoSegment | null {
  const raw = page?.config?.video_segment as {start: unknown; end: unknown} | undefined;
  if (!raw) return null;
  const start = Number(raw.start);
  const end = Number(raw.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return {start, end};
}

/**
 * Every position along a line, starting with the one you are given.
 *
 * Used to step through a tutor answer on the board. Returns `n + 1` positions
 * for `n` legal moves, and stops at the first move that will not play — though
 * the server has already validated these, so that is a belt-and-braces guard
 * rather than an expected path.
 */
export function positionsAlongLine(fen: string, line: string[]): string[] {
  let game: InstanceType<typeof Chess>;
  try {
    game = new Chess(fen);
  } catch {
    return [];
  }

  const positions = [game.fen()];
  for (const move of line ?? []) {
    const uci = parseUci(move ?? '');
    if (!uci) break;
    try {
      const played = game.move({
        from: uci.from,
        to: uci.to,
        promotion: uci.promotion ?? 'q',
      });
      if (!played) break;
    } catch {
      break;
    }
    positions.push(game.fen());
  }
  return positions;
}

/**
 * Squares the piece on `square` may legally move to.
 *
 * Click-to-move is implemented here rather than left to chessboard-package:
 * its own click handling keeps the selection in props supplied by its wrapper,
 * and in this app that state never updates — clicking a piece highlights
 * nothing, so the board reads as dead even though the move itself lands.
 */
export function legalDestinations(fen: string, square: string): string[] {
  if (!/^[a-h][1-8]$/.test(square || '')) return [];
  try {
    const game = new Chess(fen);
    return game
      .moves({square: square as never, verbose: true})
      .map((m: {to: string}) => m.to);
  } catch {
    return [];
  }
}

/**
 * The square the solving piece starts on, from the scripted first move.
 *
 * Used as the "show me where" hint when the course was generated before hints
 * were stored on the page, so older courses still get one.
 */
export function firstMoveSquare(moves: CourseMove[] | null): string {
  const first = expectedMoveForPly(moves, 1);
  const uci = (first?.move ?? '').trim().toLowerCase();
  return /^[a-h][1-8]/.test(uci) ? uci.slice(0, 2) : '';
}

export function expectedMoveForPly(
  moves: CourseMove[] | null,
  ply: number,
): CourseMove | undefined {
  return (moves ?? []).find(m => m.ply === ply);
}

function parseUci(uci: string) {
  const match = uci.trim().toLowerCase().match(/^([a-h][1-8])([a-h][1-8])([nbrq])?$/);
  if (!match) return null;
  return {from: match[1], to: match[2], promotion: match[3]};
}

/**
 * The `single_variant` contract: the student plays odd plies, the engine replies
 * on even plies, and the line ends on an odd ply. A move that is not the
 * scripted one is rejected without mutating the position.
 */
export function applyStudentMove(
  fen: string,
  moves: CourseMove[] | null,
  ply: number,
  from: string,
  to: string,
  promotion?: string,
): MoveOutcome {
  const scripted = expectedMoveForPly(moves, ply);
  if (!scripted) return {status: 'wrong'};

  const expected = parseUci(scripted.move);
  if (!expected) return {status: 'wrong'};
  if (expected.from !== from || expected.to !== to) return {status: 'wrong'};

  const game = new Chess(fen);
  let played;
  try {
    played = game.move({from, to, promotion: promotion ?? expected.promotion ?? 'q'});
  } catch {
    return {status: 'wrong'};
  }
  if (!played) return {status: 'wrong'};

  const studentSan = played.san;
  const afterStudentFen = game.fen();

  const reply = expectedMoveForPly(moves, ply + 1);
  if (!reply) return {status: 'solved', fen: afterStudentFen, studentSan};

  const replyUci = parseUci(reply.move);
  let replySan = '';
  if (replyUci) {
    try {
      const replyPlayed = game.move({
        from: replyUci.from,
        to: replyUci.to,
        promotion: replyUci.promotion ?? 'q',
      });
      replySan = replyPlayed?.san ?? '';
    } catch {
      // A malformed scripted reply should not strand the student mid-puzzle.
      return {status: 'solved', fen: afterStudentFen, studentSan};
    }
  }
  return {
    status: 'reply',
    fen: game.fen(),
    afterStudentFen,
    replyMove: reply.move,
    studentSan,
    replySan,
    nextPly: ply + 2,
  };
}

export function nextChapterIndex(
  chapters: CourseChapter[],
  currentIndex: number,
): number | null {
  const next = currentIndex + 1;
  return next < (chapters?.length ?? 0) ? next : null;
}

export function isChapterComplete(
  chapter: CourseChapter,
  completedPageIds: Set<number>,
): boolean {
  const pages = chapter?.pages ?? [];
  return pages.length > 0 && pages.every(p => completedPageIds.has(p.id));
}

const ROLES: ChapterRoleName[] = ['setup', 'puzzle', 'answer', 'practice', 'video', 'wrapup'];

/**
 * What a chapter is. A pose-anchored course writes it on the page
 * (`config.role`); a course built before that is read from the page kind, so
 * it still plays chapters in order exactly as it always did.
 */
export function chapterRole(chapter?: CourseChapter | null): ChapterRoleName {
  const page = chapter?.pages?.[0];
  const role = page?.config?.role;
  if (role && ROLES.includes(role)) return role;
  return page && isPuzzlePage(page) ? 'puzzle' : 'video';
}

/** A set-up video that runs straight into its puzzle opens the puzzle itself. */
export function opensPuzzleNext(chapters: CourseChapter[], index: number): boolean {
  const next = nextChapterIndex(chapters, index);
  return (
    chapterRole(chapters[index]) === 'setup' &&
    next != null &&
    chapterRole(chapters[next]) === 'puzzle'
  );
}

/**
 * The coach's answer to the posed puzzle at `index`: the chapter right after
 * it. A practice puzzle has none — when it splits an answer in two, the video
 * after it is the rest of the earlier puzzle's answer, not its own.
 */
export function answerChapterIndex(chapters: CourseChapter[], index: number): number | null {
  if (chapterRole(chapters[index]) !== 'puzzle') return null;
  const next = nextChapterIndex(chapters, index);
  return next != null && chapterRole(chapters[next]) === 'answer' ? next : null;
}

/** What the Continue button offers. */
export function continueLabel(chapters: CourseChapter[], index: number): string {
  return answerChapterIndex(chapters, index) != null ? "Watch the coach's answer" : 'Continue';
}

/**
 * The video chapter playing `seconds` of the recording. A course assembled
 * from several uploads has one clock per part, so 2:05 of part 2 is not 2:05
 * of part 1: when the recording is known it has to match as well.
 */
export function videoChapterAt(
  chapters: CourseChapter[],
  seconds: number,
  videoCourseId?: number | null,
): number {
  return (chapters ?? []).findIndex(chapter => {
    const page = chapter?.pages?.[0];
    const segment = page ? getVideoSegment(page) : null;
    if (!segment) return false;
    const own = page?.config?.video_course_id;
    if (videoCourseId != null && own != null && own !== videoCourseId) return false;
    return seconds >= segment.start && seconds <= segment.end;
  });
}

/** How much of the set-up "See the set-up again" replays before the question ends. */
export const SETUP_REPLAY_SEC = 30;

/**
 * Where "See the set-up again" goes for the puzzle at `index`: the video
 * chapter holding the moment the coach finished asking, a little before it.
 */
export function setupReplay(
  chapters: CourseChapter[],
  index: number,
): {index: number; startAt: number} | null {
  const config = chapters?.[index]?.pages?.[0]?.config;
  const poseEnd = Number(config?.pose_end);
  if (config?.pose_end == null || !Number.isFinite(poseEnd)) return null;
  // Searched backwards from the puzzle: the set-up is the video before it,
  // and its window ends exactly where the question does.
  for (let i = index - 1; i >= 0; i--) {
    const page = chapters[i]?.pages?.[0];
    const segment = page ? getVideoSegment(page) : null;
    if (!segment) continue;
    const own = page?.config?.video_course_id;
    if (config?.video_course_id != null && own != null && own !== config.video_course_id) continue;
    if (poseEnd >= segment.start - 0.5 && poseEnd <= segment.end + 0.5) {
      return {index: i, startAt: Math.max(segment.start, poseEnd - SETUP_REPLAY_SEC)};
    }
  }
  return null;
}

/** "Mating pattern · Greco's mate", from the puzzle's analysis; '' without one. */
export function puzzleTypeLabel(config?: CoursePageConfig | null): string {
  const found = config?.analysis;
  if (!found?.type) return '';
  const type = found.type.charAt(0).toUpperCase() + found.type.slice(1);
  return found.pattern ? `${type} · ${found.pattern}` : type;
}

